class DynoCoBundleColorMix extends HTMLElement {
  connectedCallback() {
    this.abortController?.abort();
    this.abortController = new AbortController();
    this.initialize();
  }

  disconnectedCallback() {
    this.abortController?.abort();
  }

  initialize() {
    this.toggle = this.querySelector('[data-mix-toggle]');
    this.panel = this.querySelector('[data-mix-panel]');
    this.status = this.querySelector('[data-mix-status]');
    this.instruction = this.querySelector('[data-mix-instruction]');
    this.propertyInput = this.querySelector('[data-mix-property]');
    this.preorderPropertyInput = this.querySelector('[data-mix-preorder-property]');
    this.colorInputs = Array.from(this.querySelectorAll('[data-mix-color]'));
    this.scope = this.closest('.shopify-section') || document;
    this.picker = this.scope.querySelector('variant-picker');
    this.form = document.getElementById(this.dataset.formId);
    this.variantMap = this.readVariantMap();
    this.defaultAddToCartText = this.addToCartTextNode()?.textContent?.trim() || 'In den Warenkorb';

    if (!this.toggle || !this.panel || !this.status || !this.propertyInput || !this.preorderPropertyInput || !this.form) return;

    this.stateKey = this.dataset.formId;
    window.__dynocoBundleColorMixState ||= {};
    this.state = window.__dynocoBundleColorMixState[this.stateKey] || { enabled: false, colors: {}, requiredTotal: null };
    window.__dynocoBundleColorMixState[this.stateKey] = this.state;
    this.restoreState();

    const signal = this.abortController.signal;
    this.toggle.addEventListener('change', () => this.handleToggle(), { signal });
    this.colorInputs.forEach((input) => {
      input.addEventListener('input', () => this.handleColorInput(input), { signal });
      input.addEventListener('change', () => this.handleColorInput(input), { signal });
    });
    this.picker?.addEventListener('change', () => window.setTimeout(() => this.sync(), 0), { signal });
    this.form.addEventListener('input', (event) => { if (event.target?.name === 'quantity') this.sync(); }, { signal });
    this.form.addEventListener('change', (event) => { if (event.target?.name === 'quantity') this.sync(); }, { signal });
    window.addEventListener('submit', (event) => { if (event.target === this.form) this.handleSubmit(event); }, { capture: true, signal });
    this.sync();
  }

  readVariantMap() {
    try { return JSON.parse(this.querySelector('[data-variant-map]')?.textContent || '{}'); }
    catch (error) { console.error('DynoCo Farbmischung: Variantenzuordnung konnte nicht gelesen werden.', error); return {}; }
  }

  restoreState() {
    this.toggle.checked = Boolean(this.state.enabled);
    this.colorInputs.forEach((input) => { input.value = String(this.state.colors[input.dataset.mixColor] || 0); });
  }

  persistState() {
    this.state.enabled = this.toggle.checked;
    this.state.colors = {};
    this.colorInputs.forEach((input) => { this.state.colors[input.dataset.mixColor] = this.numberValue(input); });
  }

  numberValue(input) {
    const parsed = Number.parseInt(input.value, 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
  }

  resetDistribution() {
    this.colorInputs.forEach((input) => { input.value = '0'; });
    this.state.colors = {};
  }

  selectedOptionValue(optionName) {
    if (!this.picker) return '';
    const checked = Array.from(this.picker.querySelectorAll('input[type="radio"]:checked')).find((input) => input.name.startsWith(optionName + '-'));
    if (checked) return checked.value;
    const select = Array.from(this.picker.querySelectorAll('select')).find((input) => input.name === 'options[' + optionName + ']');
    return select?.value || '';
  }

  formatValue() { return this.selectedOptionValue(this.dataset.formatOption); }
  bundleAmount() { return Number.parseInt(this.selectedOptionValue(this.dataset.quantityOption), 10) || 0; }

  orderQuantity() {
    const quantity = Number.parseInt(this.form?.querySelector('[name="quantity"]')?.value || '1', 10);
    return Number.isFinite(quantity) && quantity > 0 ? quantity : 1;
  }

  requiredTotal() { return this.bundleAmount() * this.orderQuantity(); }
  currentTotal() { return this.colorInputs.reduce((sum, input) => sum + this.numberValue(input), 0); }

  handleToggle() {
    if (this.toggle.checked) this.defaultAddToCartText = this.addToCartTextNode()?.textContent?.trim() || this.defaultAddToCartText;
    this.persistState();
    if (!this.toggle.checked) {
      this.restoreSelectedVariant();
      this.setAddToCartText(this.defaultAddToCartText);
    }
    this.sync();
  }

  handleColorInput(input) {
    const maximum = this.requiredTotal();
    let value = this.numberValue(input);
    if (maximum > 0 && value > maximum) value = maximum;
    input.value = String(value);
    this.persistState();
    this.sync();
  }

  colorFieldset() {
    if (!this.picker) return null;
    return Array.from(this.picker.querySelectorAll('fieldset')).find((fieldset) => (fieldset.querySelector('legend')?.textContent?.trim() || '').startsWith(this.dataset.colorOption)) || null;
  }

  acceleratedCheckout() { return this.form?.querySelector('[ref="acceleratedCheckoutButtonContainer"], shopify-accelerated-checkout'); }
  addToCartTextNode() { return this.form?.querySelector('.add-to-cart-text__content > span > span') || null; }
  setAddToCartText(text) { const node = this.addToCartTextNode(); if (node && text) node.textContent = text; }

  variantData(colorValue) {
    const key = this.formatValue() + '|' + String(this.bundleAmount()) + '|' + colorValue;
    const entry = this.variantMap[key];
    if (!entry) return null;
    if (typeof entry === 'string') return { id: entry, inventoryQuantity: 0, inventoryManagement: null, inventoryPolicy: null, available: true };
    return entry;
  }

  mixAvailability() {
    const amount = this.bundleAmount();
    const preorderColors = [];
    const unavailableColors = [];
    if (amount <= 0) return { requiresPreorder: false, preorderColors, unavailableColors };

    this.colorInputs.forEach((input) => {
      const selectedAmount = this.numberValue(input);
      if (selectedAmount <= 0) return;
      const color = input.dataset.mixColor;
      const variant = this.variantData(color);
      if (!variant) { unavailableColors.push(color); return; }
      const inventoryManagement = String(variant.inventoryManagement || '').toLowerCase();
      if (inventoryManagement !== 'shopify') return;
      const inventoryQuantity = Math.max(0, Number.parseInt(variant.inventoryQuantity, 10) || 0);
      const neededBundles = Math.ceil(selectedAmount / amount);
      if (inventoryQuantity >= neededBundles) return;
      if (String(variant.inventoryPolicy || '').toLowerCase() === 'continue') preorderColors.push(color);
      else unavailableColors.push(color);
    });

    return { requiresPreorder: preorderColors.length > 0, preorderColors, unavailableColors };
  }

  sync() {
    const enabled = this.toggle.checked;
    const required = this.requiredTotal();
    const previousRequired = Number.parseInt(this.state.requiredTotal, 10);
    const hadRequiredTotal = Number.isFinite(previousRequired) && previousRequired > 0;
    const staleDistribution = !hadRequiredTotal && required > 0 && this.currentTotal() > required;
    if (required > 0 && ((hadRequiredTotal && previousRequired !== required) || staleDistribution)) this.resetDistribution();
    this.state.requiredTotal = required;

    const total = this.currentTotal();
    const remaining = required - total;
    const bundle = this.bundleAmount();
    const orderQuantity = this.orderQuantity();
    const distributionComplete = required > 0 && total === required;
    const inventory = distributionComplete ? this.mixAvailability() : { requiresPreorder: false, preorderColors: [], unavailableColors: [] };

    this.panel.hidden = !enabled;
    this.colorFieldset()?.classList.toggle('dynoco-color-option--mix-active', enabled);
    const accelerated = this.acceleratedCheckout();
    if (accelerated) accelerated.hidden = enabled;
    this.colorInputs.forEach((input) => {
      input.max = String(required || 0);
      input.setAttribute('aria-invalid', enabled && total > required ? 'true' : 'false');
    });

    if (!enabled) {
      this.propertyInput.disabled = true;
      this.propertyInput.value = '';
      this.preorderPropertyInput.disabled = true;
      this.preorderPropertyInput.value = '';
      this.status.removeAttribute('data-complete');
      this.status.removeAttribute('data-error');
      this.status.removeAttribute('data-preorder');
      return;
    }

    this.instruction.textContent = orderQuantity > 1
      ? 'Verteilen Sie insgesamt ' + required + ' Dokumentenmappen (' + orderQuantity + ' × ' + bundle + 'er Bundle).'
      : 'Verteilen Sie insgesamt ' + required + ' Dokumentenmappen auf die gewünschten Farben.';

    this.status.removeAttribute('data-complete');
    this.status.removeAttribute('data-error');
    this.status.removeAttribute('data-preorder');

    if (required <= 0) { this.status.textContent = 'Bitte wählen Sie zuerst eine Bundle-Menge.'; this.status.setAttribute('data-error', 'true'); }
    else if (remaining > 0) this.status.textContent = 'Noch ' + remaining + ' Stück verteilen.';
    else if (remaining < 0) { this.status.textContent = Math.abs(remaining) + ' Stück zu viel ausgewählt.'; this.status.setAttribute('data-error', 'true'); }
    else if (inventory.unavailableColors.length > 0) { this.status.textContent = 'Aktuell nicht bestellbar: ' + inventory.unavailableColors.join(', ') + '.'; this.status.setAttribute('data-error', 'true'); }
    else if (inventory.requiresPreorder) { this.status.textContent = 'Verteilung vollständig. Vorbestellung für: ' + inventory.preorderColors.join(', ') + '.'; this.status.setAttribute('data-complete', 'true'); this.status.setAttribute('data-preorder', 'true'); }
    else { this.status.textContent = 'Verteilung vollständig und lagernd.'; this.status.setAttribute('data-complete', 'true'); }

    const valid = distributionComplete && inventory.unavailableColors.length === 0;
    const preorder = valid && inventory.requiresPreorder;
    this.propertyInput.disabled = !valid;
    this.propertyInput.value = valid ? this.summaryValue() : '';
    this.preorderPropertyInput.disabled = !preorder;
    this.preorderPropertyInput.value = preorder ? 'Vorbestellung' : '';
    this.setAddToCartText(preorder ? 'Vorbestellen' : 'In den Warenkorb');
    this.persistState();
  }

  summaryValue() {
    return this.colorInputs.map((input) => ({ color: input.dataset.mixColor, amount: this.numberValue(input) })).filter((item) => item.amount > 0).map((item) => item.color + ': ' + item.amount).join(' | ');
  }

  setVariant(colorValue) {
    const variant = this.variantData(colorValue);
    const variantInput = this.form?.querySelector('input[name="id"]');
    if (variant?.id && variantInput) variantInput.value = variant.id;
  }

  restoreSelectedVariant() {
    const selectedColor = this.selectedOptionValue(this.dataset.colorOption);
    if (selectedColor) this.setVariant(selectedColor);
  }

  handleSubmit(event) {
    if (!this.toggle.checked) return;
    const required = this.requiredTotal();
    const total = this.currentTotal();
    if (required <= 0 || total !== required) {
      event.preventDefault();
      event.stopImmediatePropagation();
      this.status.textContent = 'Bitte verteilen Sie genau ' + required + ' Stück. Aktuell ausgewählt: ' + total + '.';
      this.status.setAttribute('data-error', 'true');
      this.status.removeAttribute('data-complete');
      this.status.removeAttribute('data-preorder');
      this.colorInputs[0]?.focus();
      return;
    }

    const inventory = this.mixAvailability();
    if (inventory.unavailableColors.length > 0) {
      event.preventDefault();
      event.stopImmediatePropagation();
      this.status.textContent = 'Aktuell nicht bestellbar: ' + inventory.unavailableColors.join(', ') + '.';
      this.status.setAttribute('data-error', 'true');
      this.status.removeAttribute('data-complete');
      this.status.removeAttribute('data-preorder');
      return;
    }

    this.propertyInput.disabled = false;
    this.propertyInput.value = this.summaryValue();
    this.preorderPropertyInput.disabled = !inventory.requiresPreorder;
    this.preorderPropertyInput.value = inventory.requiresPreorder ? 'Vorbestellung' : '';
    this.setVariant('Farben mischen');
    this.setAddToCartText(inventory.requiresPreorder ? 'Vorbestellen' : 'In den Warenkorb');
  }
}

if (!customElements.get('dynoco-bundle-color-mix')) customElements.define('dynoco-bundle-color-mix', DynoCoBundleColorMix);
